import React from 'react'
import {defineMessages, useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'

import {NoStyleLink} from '@vio/atlas/helpers/NoStyleLink'
import {cssTheme} from '@vio/atlas/themes'
import {resolveCapabilities} from '@vio/core/src/brand/capabilities/resolveCapabilities'
import {Brand} from '@vio/core/src/brand/types'
import {getLocalizedFooterUrl} from '@vio/core/src/brand/utils'
import {getLanguageCode} from '@vio/core/src/localization/services/locale'

import {openCookiePreferencesCenter} from '../../search/cookies/utils'

const messages = defineMessages({
  cookiePreferences: {
    id: 'cookiePreferences',
    defaultMessage: 'Cookie preferences'
  },
  cookiePolicy: {
    id: 'cookiePolicy',
    defaultMessage: 'Cookie policy'
  },
  privacy: {
    id: 'privacyPolicy',
    defaultMessage: 'Privacy policy'
  },
  terms: {
    id: 'termsOfUse',
    defaultMessage: 'Terms of use'
  },
  howToCancel: {
    id: 'howToCancel',
    defaultMessage: 'How to cancel'
  }
})

export interface FooterLinkProps {
  label: string
  href?: string
  onClick?: () => void
}

const StyledLink = styled(NoStyleLink)`
  ${cssTheme.typography.bodyM};

  &:hover {
    text-decoration: underline;
  }

  &:active {
    text-decoration: none;
  }
`

export const FooterLink = ({label, href, onClick}: FooterLinkProps) =>
  href ? (
    <StyledLink target="_blank" href={href}>
      {label}
    </StyledLink>
  ) : (
    <StyledLink onClick={onClick}>{label}</StyledLink>
  )

/** Cookie preferences, how to cancel, cookie policy, privacy policy and terms of use, minus the ones the brand doesn't declare. */
export const useFooterLegalLinks = (
  brand: Brand,
  showHowToCancel = false
): FooterLinkProps[] => {
  const {formatMessage} = useIntl()
  const languageCode = useSelector(getLanguageCode)
  const {compliance} = resolveCapabilities(brand.capabilities, undefined, {
    skipClientOnly: true
  })

  const links: FooterLinkProps[] = [
    {
      label: formatMessage(messages.howToCancel),
      href: showHowToCancel
        ? getLocalizedFooterUrl(brand.links.howToCancelUrl, languageCode)
        : undefined
    },
    {
      label: formatMessage(messages.cookiePolicy),
      href: getLocalizedFooterUrl(brand.links.cookiePolicyUrl, languageCode)
    },
    {
      label: formatMessage(messages.privacy),
      href: getLocalizedFooterUrl(brand.links.privacyPolicyUrl, languageCode)
    },
    {
      label: formatMessage(messages.terms),
      href: getLocalizedFooterUrl(brand.links.termsOfUseUrl, languageCode)
    }
  ]
  if (compliance.cookieConsent.enabled) {
    links.unshift({
      label: formatMessage(messages.cookiePreferences),
      onClick: openCookiePreferencesCenter
    })
  }
  return links.filter(link => link.href || link.onClick)
}
